import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { downloadCsv } from './csv';
import { PanelHead } from './panel';
import { metricText, type Fmt } from './format';
import type { CampaignRow, PeriodReport } from './use-report';

const COLUMNS = [
  'campaign',
  'investment',
  'impressions',
  'clicks',
  'ctr',
  'leads',
  'cpl',
  'qualified',
  'cpql',
  'meetings',
  'cpMeeting',
  'reserved',
  'cpa',
] as const;

/** Rows shown before the reader asks for all: the table sorts by spend, then cases, so the top is where decisions are made. */
const TOP_N = 50;

const fold = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function cellValues(r: CampaignRow, fmt: Fmt, noCampaign: string): string[] {
  const cost = (i: number) => {
    const m = r.economics.costPerLevel[i];
    return m ? metricText(m, (v) => fmt.czk(v)) : '—';
  };
  return [
    r.label ?? noCampaign,
    r.spend ? fmt.czk(r.spend.costCzk) : '—',
    r.spend ? fmt.count(r.spend.impressions) : '—',
    r.spend ? fmt.count(r.spend.clicks) : '—',
    metricText(r.ctr, (v) => fmt.pct(v, 2)),
    fmt.int(r.metrics.leads),
    cost(0),
    fmt.int(r.metrics.reached[1] ?? 0),
    cost(1),
    fmt.int(r.metrics.reached[2] ?? 0),
    cost(2),
    fmt.int(r.metrics.reserved),
    cost(6),
  ];
}

/** Every CRM campaign of the period with its mapped spend and the cumulative costs per result; searchable, exportable. */
export function CampaignTable({ report, fmt, exportName }: { report: PeriodReport; fmt: Fmt; exportName: string }) {
  const { t } = useTranslation(['performance', 'common']);
  const [q, setQ] = useState('');
  const [showAll, setShowAll] = useState(false);
  const allRows = useMemo(() => {
    const needle = fold(q.trim());
    return report.campaigns.filter((r) => !needle || fold(r.label ?? '').includes(needle));
  }, [report.campaigns, q]);
  const rows = showAll ? allRows : allRows.slice(0, TOP_N);
  const hidden = allRows.length - rows.length;
  const headers = COLUMNS.map((c) => t(`dashboard.campaigns.${c}`));

  const exportCsv = () =>
    downloadCsv(
      exportName,
      headers,
      allRows.map((r) => cellValues(r, fmt, t('dashboard.campaigns.noCampaign'))),
    );

  return (
    <section className="pa-panel pa-table-panel" id="pa-campaigns">
      <PanelHead title={t('dashboard.campaigns.title')} subtitle={t('dashboard.campaigns.subtitle')}>
        <label className="pa-field pa-campaign-search">
          <span>{t('dashboard.campaigns.search')}</span>
          <input
            type="search"
            value={q}
            placeholder={t('dashboard.campaigns.searchPlaceholder')}
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
      </PanelHead>
      {rows.length === 0 ? (
        <p className="pa-empty">{t('dashboard.campaigns.empty')}</p>
      ) : (
        <div className="pa-table-scroll" tabIndex={0}>
          <table className="pa-table hp-table pa-campaign-table">
            <thead>
              <tr>
                {headers.map((h) => (
                  <th key={h} scope="col">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const values = cellValues(r, fmt, t('dashboard.campaigns.noCampaign'));
                return (
                  <tr key={r.campaignOption ?? 'none'}>
                    {values.map((v, i) => (
                      <td key={COLUMNS[i]}>{i === 0 ? <strong>{v}</strong> : i === 5 ? <b>{v}</b> : v}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {hidden > 0 && (
        <div className="hp-card__body">
          <button type="button" className="hp-button hp-button--quiet" onClick={() => setShowAll(true)}>
            {t('dashboard.campaigns.showAll', { count: hidden })}
          </button>
        </div>
      )}
      {report.unmappedSpend.costCzk > 0 && (
        <p className="pa-footnote">
          {t('dashboard.campaigns.unmapped', { czk: fmt.czk(report.unmappedSpend.costCzk) })}
        </p>
      )}
      <p className="pa-footnote">{t('dashboard.campaigns.footnote')}</p>
      <div className="hp-card__body">
        <button
          type="button"
          className="hp-button hp-button--secondary"
          onClick={exportCsv}
          disabled={rows.length === 0}
        >
          {t('dashboard.exportCsv')}
        </button>
      </div>
    </section>
  );
}
